import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, rtdb, authReady } from '../../services/firebase';
import { doc, setDoc, serverTimestamp, onSnapshot, collection, query, where, orderBy, getDocs, documentId, getDoc } from 'firebase/firestore';
import { ref, onValue, set, onDisconnect, serverTimestamp as rtdbTimestamp } from 'firebase/database';
import { onAuthStateChanged, signInWithEmailAndPassword } from 'firebase/auth';
import { requestNotificationPermission } from '../../services/notifications';
import { getSavedSession, clearSession } from '../../services/autoLogin';
import { Sidebar } from './Sidebar';
import { VideoCall } from './VideoCall';
import { ChatWindow } from './ChatWindow';
import type { ChatContact } from './types';
import './Themes.css'
import './chat.css';

import { Board } from './Board';

// ── Splash Screen ──
const Splash = ({ visible }: { visible: boolean }) => (
  <div className={`tolkx-splash ${!visible ? 'tolkx-splash--hidden' : ''}`}>
    <div className="tolkx-splash__logo">TolkX</div>
    <div className="tolkx-splash__dots">
      <div className="tolkx-splash__dot" />
      <div className="tolkx-splash__dot" />
      <div className="tolkx-splash__dot" />
    </div>
  </div>
);

// ── Skeleton chat items ──
const SkeletonList = () => (
  <>
    {[55, 70, 45, 65, 50].map((w, i) => (
      <div className="skeleton-chat-item" key={i}>
        <div className="skeleton skeleton-chat-item__avatar" />
        <div className="skeleton-chat-item__body">
          <div className="skeleton skeleton-chat-item__name" style={{ width: `${w}%` }} />
          <div className="skeleton skeleton-chat-item__preview" style={{ width: `${Math.min(w + 20, 90)}%` }} />
        </div>
      </div>
    ))}
  </>
);

export const ChatPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [activeChat, setActiveChat] = useState<ChatContact | null>(null);
  const [boardOpen, setBoardOpen] = useState(false);
  const [globalIncomingCall, setGlobalIncomingCall] = useState<{
    callId: string; callType: 'audio' | 'video';
    callerName: string; callerPhoto?: string; callerUid: string;
    chatId: string;
  } | null>(null);
  const [globalCallMinimized, setGlobalCallMinimized] = useState(false);
  const [chats, setChats] = useState<ChatContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [splashVisible, setSplashVisible] = useState(true);
  const redirectTimeout = useRef<any>(null);

  // ── Auth check + login silencioso ──
  useEffect(() => {
    let unsub = () => {};

    authReady.then(() => {
      unsub = onAuthStateChanged(auth, async (user) => {
        if (redirectTimeout.current) {
          clearTimeout(redirectTimeout.current);
          redirectTimeout.current = null;
        }

        if (user) {
          setCurrentUser(user);
          setAuthChecked(true);
          setIsAuthReady(true);
          requestNotificationPermission(user.uid);

          // ── Presencia con RTDB onDisconnect (funciona en móvil/PWA) ──
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const hideOnline = userDoc.data()?.hideOnlineStatus ?? false;

          const presenceRef = ref(rtdb, `presence/${user.uid}`);
          const firestoreRef = doc(db, 'users', user.uid);

          if (!hideOnline) {
            // Marcar online en RTDB y Firestore
            await set(presenceRef, { online: true, lastSeen: rtdbTimestamp() });
            await setDoc(firestoreRef, { online: true, lastSeen: serverTimestamp() }, { merge: true });

            // Al desconectarse RTDB lo marca offline automáticamente
            await onDisconnect(presenceRef).set({ online: false, lastSeen: rtdbTimestamp() });

            // Escuchar cambios en RTDB y sincronizar a Firestore
            onValue(presenceRef, (snap) => {
              const data = snap.val();
              if (data && data.online === false) {
                setDoc(firestoreRef, { online: false, lastSeen: serverTimestamp() }, { merge: true });
              }
            });

            // PWA/mobile fallback — marcar offline cuando el usuario sale
            const markOffline = () => {
              set(presenceRef, { online: false, lastSeen: rtdbTimestamp() });
              setDoc(firestoreRef, { online: false, lastSeen: serverTimestamp() }, { merge: true });
            };
            const markOnline = () => {
              set(presenceRef, { online: true, lastSeen: rtdbTimestamp() });
              setDoc(firestoreRef, { online: true, lastSeen: serverTimestamp() }, { merge: true });
            };

            // visibilitychange: cuando minimizan o cambian de tab
            const handleVisibility = () => {
              if (document.visibilityState === 'hidden') markOffline();
              else markOnline();
            };
            // pagehide: cuando cierran la app en móvil
            const handlePageHide = () => markOffline();

            document.addEventListener('visibilitychange', handleVisibility);
            window.addEventListener('pagehide', handlePageHide);
          } else {
            await set(presenceRef, { online: false, lastSeen: rtdbTimestamp() });
            await onDisconnect(presenceRef).set({ online: false, lastSeen: rtdbTimestamp() });
            await setDoc(firestoreRef, { online: false, lastSeen: serverTimestamp() }, { merge: true });
          }
          return; // ← usuario autenticado, no seguir
        }

        // Firebase no encontró sesión — intentar login silencioso
        const saved = getSavedSession();
        if (saved) {
          try {
            // Login silencioso con credenciales guardadas
            await signInWithEmailAndPassword(auth, saved.email, saved.password);
            // onAuthStateChanged se disparará de nuevo con el usuario
            return;
          } catch {
            // Credenciales inválidas (password cambiado, cuenta eliminada, etc.)
            clearSession();
          }
        }

        // No hay sesión ni credenciales guardadas — ir al login
        setAuthChecked(true);
        redirectTimeout.current = setTimeout(() => navigate('/login'), 300);
      });
    });

    return () => {
      unsub();
      if (redirectTimeout.current) clearTimeout(redirectTimeout.current);
    };
  }, [navigate]);

  // ── Hide splash once auth is ready ──
  useEffect(() => {
    if (isAuthReady) {
      const t = setTimeout(() => setSplashVisible(false), 300);
      return () => clearTimeout(t);
    }
  }, [isAuthReady]);

  // ── Load chats ──
  useEffect(() => {
    if (!currentUser) return;

    // Cache de perfiles de usuario para evitar re-fetches entre snapshots
    const userProfileCache = new Map<string, any>();

    const q = query(
      collection(db, 'chats'),
      where('members', 'array-contains', currentUser.uid),
      orderBy('lastMessageTime', 'desc')
    );

    const unsub = onSnapshot(q, async (snap) => {
      const chatList: ChatContact[] = [];

      // ── Separar grupos de chats 1-on-1 ──
      const groupDocs: typeof snap.docs = [];
      const directDocs: { docSnap: typeof snap.docs[0]; otherUid: string }[] = [];

      for (const docSnap of snap.docs) {
        const data = docSnap.data();
        if (data.isGroup) {
          groupDocs.push(docSnap);
        } else {
          const otherUid = data.members.find((m: string) => m !== currentUser.uid);
          if (otherUid) directDocs.push({ docSnap, otherUid });
        }
      }

      // ── Grupos: no necesitan fetch de usuario ──
      for (const docSnap of groupDocs) {
        const data = docSnap.data();
        chatList.push({
          chatId: docSnap.id,
          uid: '',
          displayName: data.groupName || 'Grupo',
          photoURL: data.groupPhoto || undefined,
          online: false,
          lastMessage: data.lastMessage || '',
          lastMessageTime: data.lastMessageTime,
          unreadCount: data.unreadCount?.[currentUser.uid] || 0,
          isGroup: true,
          members: data.members || [],
          memberProfiles: data.memberProfiles || {},
          archivedBy: data.archivedBy || {},
          pinnedBy: data.pinnedBy || {},
          deletedBy: data.deletedBy || {},
          deletedAt: data.deletedAt || {},
        });
      }

      // ── Chats 1-on-1: batch fetch de todos los usuarios en una sola query ──
      // Firestore soporta hasta 30 items en "in"; si hay más se hace en chunks
      if (directDocs.length > 0) {
        const uidsToFetch = directDocs
          .map(d => d.otherUid)
          .filter(uid => !userProfileCache.has(uid));

        // Fetch en chunks de 30 (límite de Firestore para "in")
        const CHUNK_SIZE = 30;
        for (let i = 0; i < uidsToFetch.length; i += CHUNK_SIZE) {
          const chunk = uidsToFetch.slice(i, i + CHUNK_SIZE);
          const usersSnap = await getDocs(
            query(collection(db, 'users'), where(documentId(), 'in', chunk))
          );
          usersSnap.forEach(s => userProfileCache.set(s.id, s.data()));
        }

        // Construir lista usando el cache
        for (const { docSnap, otherUid } of directDocs) {
          const data = docSnap.data();
          const profile = userProfileCache.get(otherUid);
          chatList.push({
            chatId: docSnap.id,
            uid: otherUid,
            displayName: profile?.displayName || profile?.username || 'Usuario',
            username: profile?.username || '',
            photoURL: profile?.photoURL,
            online: profile?.online || false,
            lastSeen: profile?.lastSeen || null,
            lastMessage: data.lastMessage || '',
            lastMessageTime: data.lastMessageTime,
            unreadCount: data.unreadCount?.[currentUser.uid] || 0,
            isGroup: false,
            archivedBy: data.archivedBy || {},
            pinnedBy: data.pinnedBy || {},
            deletedBy: data.deletedBy || {},
            deletedAt: data.deletedAt || {},
          });
        }
      }

      setChats(chatList);
      setLoading(false);
    });

    return () => unsub();
  }, [currentUser]);

  // ── Presencia en tiempo real via RTDB ──
  // Un solo listener por contacto en RTDB (barato: no cobra por lectura).
  // Se re-ejecuta cuando cambia la lista de chats (contactos nuevos/eliminados).
  useEffect(() => {
    if (!currentUser || chats.length === 0) return;

    const directChats = chats.filter(c => !c.isGroup && c.uid);
    const cleanups: (() => void)[] = [];

    directChats.forEach(chat => {
      const presenceRef = ref(rtdb, `presence/${chat.uid}`);
      const unsub = onValue(presenceRef, (snap) => {
        const data = snap.val();
        if (!data) return;

        setChats(prev =>
          prev.map(c =>
            c.uid === chat.uid
              ? { ...c, online: data.online ?? false, lastSeen: data.lastSeen ?? c.lastSeen }
              : c
          )
        );
      });
      cleanups.push(unsub);
    });

    return () => cleanups.forEach(fn => fn());
  }, [currentUser, chats.map(c => c.uid).join(',')]);

  // ── Global incoming call listener (works regardless of activeChat) ──
  useEffect(() => {
    if (!currentUser || chats.length === 0) return;

    const unsubs: (() => void)[] = [];

    // Listen to all 1-on-1 call docs where I'm the callee
    chats.filter(c => !c.isGroup).forEach(chat => {
      const callId = [currentUser.uid, chat.uid].sort().join('_call_');
      const unsub = onSnapshot(doc(db, 'calls', callId), (snap) => {
        const data = snap.data();
        if (!data) return;
        if (data.calleeId === currentUser.uid && data.status === 'calling' && !globalIncomingCall) {
          // Vibrate on incoming call
          if (navigator.vibrate) navigator.vibrate([400, 200, 400, 200, 400]);
          setGlobalIncomingCall({
            callId,
            callType: data.callType,
            callerName: data.callerName,
            callerPhoto: data.callerPhoto,
            callerUid: data.callerId,
            chatId: chat.chatId,
          });
        }
        if (data.status === 'ended' || data.status === 'rejected') {
          setGlobalIncomingCall(null);
        }
      });
      unsubs.push(unsub);
    });

    return () => unsubs.forEach(u => u());
  }, [currentUser, chats, globalIncomingCall]);

  // Expose chats globally for forward modal
  useEffect(() => { (window as any).__tolkxChats = chats; }, [chats]);

  // ── Unread count in browser tab ──
  useEffect(() => {
    const total = chats.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
    document.title = total > 0 ? `(${total}) TolkX` : 'TolkX';
  }, [chats]);

  // ── Mobile back button handler ──
  useEffect(() => {
    window.history.replaceState({ chatOpen: false }, '');
  }, []);

  useEffect(() => {
    if (activeChat) {
      window.history.pushState({ chatOpen: true }, '');
    }
  }, [activeChat]);

  useEffect(() => {
    const handlePopState = (_e: PopStateEvent) => {
      if (activeChat) {
        setActiveChat(null);
        window.history.replaceState({ chatOpen: false }, '');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [activeChat]);

  // ── Message notifications ──
  const prevChats = useRef<ChatContact[]>([]);
  const activeChatRef = useRef<ChatContact | null>(null);
  activeChatRef.current = activeChat;

  useEffect(() => {
    if (!currentUser || chats.length === 0) return;
    if (Notification.permission !== 'granted') return;

    if (prevChats.current.length === 0) {
      prevChats.current = chats;
      return;
    }

    chats.forEach(chat => {
      const prev = prevChats.current.find(c => c.chatId === chat.chatId);
      const isActiveChat = activeChatRef.current?.chatId === chat.chatId;

      if (
        prev &&
        (chat.unreadCount ?? 0) > (prev.unreadCount ?? 0) &&
        !isActiveChat &&
        chat.lastMessage
      ) {
        const title = chat.displayName;

        const body = chat.lastMessage.startsWith('data:image')
          ? '📷 Imagen'
          : chat.lastMessage.startsWith('http') && chat.lastMessage.includes('giphy')
          ? '🎬 GIF'
          : chat.lastMessage.length > 80
          ? chat.lastMessage.slice(0, 80) + '...'
          : chat.lastMessage;

        try {
          new Notification(title, {
            body,
            icon: chat.photoURL || '/icons/icon-192x192.png',
            badge: '/icons/icon-192x192.png',
          });
        } catch(e) { console.warn('Notification error:', e); }
      }
    });

    prevChats.current = chats;
  }, [chats, currentUser]);

  if (!authChecked) return <Splash visible={true} />;
  if (!currentUser) return <Splash visible={true} />;

  return (
    <>
      <Splash visible={splashVisible} />
      {globalIncomingCall && (
        <VideoCall
          currentUser={currentUser}
          remoteUser={{ uid: globalIncomingCall.callerUid, displayName: globalIncomingCall.callerName, photoURL: globalIncomingCall.callerPhoto }}
          callType={globalIncomingCall.callType}
          chatId={globalIncomingCall.chatId}
          isIncoming
          callId={globalIncomingCall.callId}
          minimized={globalCallMinimized}
          onMinimize={() => setGlobalCallMinimized(m => !m)}
          onEnd={() => { setGlobalIncomingCall(null); setGlobalCallMinimized(false); }}
        />
      )}
      <div className="chat-app">
        <Sidebar
          currentUser={currentUser}
          chats={chats}
          activeChat={activeChat}
          onSelectChat={(chat) => { setActiveChat(chat); setBoardOpen(false); }}
          loading={loading}
          SkeletonList={SkeletonList}
          onBoardOpen={(open) => { setBoardOpen(open); if (open) setActiveChat(null); }}
        />
        {boardOpen ? (
          <div className="chat-window chat-window--visible board-panel">
            <Board
              currentUser={currentUser}
              friendUids={chats
                .filter(c => !c.isGroup && c.uid)
                .map(c => c.uid)
              }
              onClose={() => setBoardOpen(false)}
            />
          </div>
        ) : (
          <ChatWindow
            currentUser={currentUser}
            activeChat={activeChat}
            onClose={() => setActiveChat(null)}
          />
        )}
      </div>
    </>
  );
};